import { useId } from 'react';
import { track } from '@/lib/analytics';
import {
  ensaioMaterial,
  previsoesMaterial,
  type EditionMaterial,
} from '@/modules/tenant/domain/entities/materials';
import { useTenant } from '@/modules/tenant/presentation/hooks/use-tenant';

/** O material das Previsões desta edição (PDF do site ou link), ou `null`. */
export function usePrevisoesMaterial(): EditionMaterial | null {
  const { exam, edition } = useTenant();
  return previsoesMaterial(exam, edition);
}

/** O material do ensaio desta edição (PDF do site ou link), ou `null`. */
export function useEnsaioMaterial(): EditionMaterial | null {
  const { exam, edition } = useTenant();
  return ensaioMaterial(exam, edition);
}

/**
 * Os atributos do link de um material: **PDF do site baixa** direto
 * (`download`, com nome legível); **link externo abre** em outra aba.
 */
export function linkProps(material: EditionMaterial) {
  return material.downloadName
    ? { href: material.href, download: material.downloadName }
    : {
        href: material.href,
        target: '_blank',
        rel: 'noopener noreferrer',
      };
}

/**
 * O botão das Previsões Aristo (gradiente do CTA, com brilho). Baixa o PDF de
 * `materiais/<prova>/<ano>/previsoes-aristo.pdf`, ou abre o
 * `previsoesAristoUrl`; sem os dois, não existe.
 */
export function PrevisoesAristoCta({ label }: { label: string }) {
  const material = usePrevisoesMaterial();
  if (!material) return null;

  return (
    <a
      className="previsoes-cta"
      {...linkProps(material)}
      onClick={() => track({ event: 'previsoes_aristo_clicado' })}
    >
      <span className="previsoes-cta-spark" aria-hidden="true">
        ✦
      </span>
      {label}
      {!material.downloadName && (
        <span className="sr-only"> (abre em nova aba)</span>
      )}
    </a>
  );
}

/**
 * "Baixar o ensaio": baixa o PDF de `materiais/<prova>/<ano>/ensaio.pdf`, ou
 * abre o `ensaioDownloadUrl`; sem os dois, não existe. Não depende das
 * questões: com a busca fora do ar, resolver no papel é justamente a saída.
 */
export function EnsaioDownloadLink() {
  const material = useEnsaioMaterial();
  if (!material) return null;

  return (
    <a
      className="ghost"
      {...linkProps(material)}
      onClick={() => track({ event: 'ensaio_download_clicado' })}
    >
      Baixar o ensaio
      {!material.downloadName && (
        <span className="sr-only"> (abre em nova aba)</span>
      )}
    </a>
  );
}

/**
 * **A seção das Previsões Aristo — o carro-chefe**, acima das abas
 * principais do painel: selo, título e texto à esquerda, o botão com brilho à
 * direita (quebra para baixo quando a tela estreita). **Não aparece onde a
 * pessoa faz o ensaio** (`/ensaio` e a aba Ensaio antes do envio) — ver
 * `PainelPage`.
 *
 * Só aparece se a edição tem as Previsões (PDF ou link). O texto vem de
 * `content.previsoes`, que o marketing sobrescreve por prova.
 */
export function PrevisoesAristoSection() {
  const { content } = useTenant();
  const material = usePrevisoesMaterial();
  const titleId = useId();
  if (!material) return null;

  return (
    <section className="previsoes-section" aria-labelledby={titleId}>
      <div className="previsoes-copy">
        <p className="previsoes-eyebrow">
          <span aria-hidden="true">✦ </span>Previsões Aristo
        </p>
        <h2 className="previsoes-title" id={titleId}>
          {content.previsoes.title}
        </h2>
        <p className="previsoes-text">{content.previsoes.description}</p>
      </div>
      {/* À direita do texto; quebra para baixo quando a tela estreita. */}
      <PrevisoesAristoCta label={content.previsoes.ctaLabel} />
    </section>
  );
}
